import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { usePayment } from "../context/PaymentContext";
import "../styles/ReferralProgram.css";
import { useTranslation } from "react-i18next";
import PaymentModal from "./PaymentModal";

import { getApiUrl } from "../utils/config";
const API_URL = getApiUrl();

const CPAPartner = () => {
    const { isAuth, showNotification } = useAuth();
    const { profile, openAccountHistoryModal } = usePayment();
    const [partnerData, setPartnerData] = useState(null);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { t } = useTranslation();

    useEffect(() => {
        if (isAuth) {
            fetchCPAData();
        }
    }, [isAuth]);

    const fetchCPAData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/cpa-partner/stats`, {
                method: "GET",
                credentials: "include",
            });

            if (!response.ok) {
                if (response.status === 403) {
                    setError("Вы не являетесь CPA партнёром");
                    setLoading(false);
                    return;
                }
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setPartnerData(data);
                setCurrentPage(1);
            } else {
                throw new Error(data.message || "Ошибка загрузки данных");
            }
        } catch (err) {
            setError(err.message || "Ошибка загрузки данных");
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = async () => {
        if (copied || !partnerData) return;
        
        const linkValue = `${window.location.origin}/?cpa=${profile.MyuserId}`;
        
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(linkValue);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = linkValue;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);
                
                if (!successful) {
                    throw new Error('execCommand failed');
                }
            }
            
            setCopied(true);
            setTimeout(() => {
                setCopied(false);
            }, 2000);
            
        } catch (error) {
            console.error('Ошибка при копировании:', error);
            setError('Не удалось скопировать ссылку. Попробуйте выделить и скопировать вручную.');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleRequestPayout = async () => {
        if (!partnerData || partnerData.stats.usersWithoutPayout === 0) {
            showNotification("Нет пользователей для выплаты", "error");
            return;
        }

        try {
            const response = await fetch(`${API_URL}/api/cpa-partner/request-payout`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ currency: "USD" })
            });

            const data = await response.json();

            if (data.success) {
                showNotification(data.message || "Заявка на выплату создана", "success");
                fetchCPAData(); // Обновляем данные
            } else {
                showNotification(data.message || "Ошибка при создании заявки", "error");
            }
        } catch (err) {
            showNotification("Ошибка при создании заявки на выплату", "error");
        }
    };

    if (!isAuth) {
        return <div className="profile-container">Необходима авторизация</div>;
    }

    if (loading) {
        return (
            <div className="profile-container">
                <div className="profile-loading">
                    <div className="loading-spinner"></div>
                    <p>Загрузка данных...</p>
                </div>
            </div>
        );
    }

    if (error && !partnerData) {
        return (
            <div className="profile-container">
                <div className="profile-error">
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!partnerData) {
        return <div className="profile-container">Данные не загружены</div>;
    }

    const { partner, users, stats, withdrawals } = partnerData;

    // Пагинация пользователей
    const totalPages = Math.ceil(users.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedUsers = users.slice(startIndex, startIndex + itemsPerPage);

    return (
        <div className="referral-container">
            <h2 className="referral-title">CPA Партнёрская программа</h2>
            
            <div className="referral-description">
                Ваша ставка: <b style={{ color: '#28bc10' }}>${partner.cpaRate}</b> за каждого пользователя с депозитом
            </div>

            {/* CPA ссылка */}
            <div className="referral-block">
                <div className="referral-link-label">Ваша CPA ссылка</div>
                <div className="referral-copy-wrapper">
                    <input
                        type="text"
                        className="referral-copy-input"
                        readOnly
                        value={`${window.location.origin}/?cpa=${profile.MyuserId}`}
                        onClick={(e) => e.target.select()}
                        title="Нажмите для выделения ссылки"
                    />
                    <button
                        className="referral-copy-btn"
                        onClick={handleCopy}
                        disabled={copied}
                    >
                        {copied ? "Скопировано!" : "Копировать"}
                    </button>
                </div>
                <div className="referral-link-hint">Поделитесь этой ссылкой для привлечения пользователей</div>
            </div>

            {/* Общая статистика */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: 16, 
                marginBottom: 32 
            }}>
                <div style={{ 
                    background: 'linear-gradient(135deg,#28bc10 60%,#4891ff 100%)', 
                    borderRadius: 14, 
                    padding: 20, 
                    color: '#fff', 
                    fontWeight: 700 
                }}>
                    <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>Всего пользователей</div>
                    <div style={{ fontSize: 32, fontWeight: 900 }}>{stats.totalUsers}</div>
                </div>
                <div style={{ 
                    background: 'linear-gradient(135deg,#4891ff 60%,#28bc10 100%)', 
                    borderRadius: 14, 
                    padding: 20, 
                    color: '#fff', 
                    fontWeight: 700 
                }}>
                    <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>С депозитами</div>
                    <div style={{ fontSize: 32, fontWeight: 900 }}>{stats.usersWithDeposits}</div>
                </div>
                <div style={{ 
                    background: 'linear-gradient(135deg,#ff6b6b 60%,#ee5a6f 100%)', 
                    borderRadius: 14, 
                    padding: 20, 
                    color: '#fff', 
                    fontWeight: 700 
                }}>
                    <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>Без выплаты</div>
                    <div style={{ fontSize: 32, fontWeight: 900 }}>{stats.usersWithoutPayout}</div>
                </div>
                <div style={{ 
                    background: 'linear-gradient(135deg,#feca57 60%,#ff9ff3 100%)', 
                    borderRadius: 14, 
                    padding: 20, 
                    color: '#fff', 
                    fontWeight: 700 
                }}>
                    <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8 }}>Доступно к выплате</div>
                    <div style={{ fontSize: 32, fontWeight: 900 }}>${stats.totalEarnings.toFixed(2)}</div>
                </div>
            </div>

            {/* Кнопка запроса выплаты */}
            {stats.usersWithoutPayout > 0 && (
                <div style={{ marginBottom: 32 }}>
                    <button
                        onClick={handleRequestPayout}
                        style={{
                            padding: '16px 32px',
                            borderRadius: 12,
                            border: 'none',
                            background: 'linear-gradient(135deg,#28bc10 0%,#4891ff 100%)',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: 18,
                            cursor: 'pointer',
                            boxShadow: '0 4px 16px 0 #28bc1088'
                        }}
                    >
                        Получить выплату (${stats.totalEarnings.toFixed(2)})
                    </button>
                </div>
            )}

            {/* Общая статистика депозитов по валютам */}
            {Object.keys(stats.totalDepositsByCurrency).length > 0 && (
                <div style={{ marginBottom: 32 }}>
                    <h3 style={{ color: '#fff', marginBottom: 16 }}>Общая статистика депозитов</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {Object.entries(stats.totalDepositsByCurrency).map(([cur, data]) => (
                            <div key={cur} style={{
                                background: 'linear-gradient(120deg,#23232e 80%,#2e2e44 100%)',
                                borderRadius: 12,
                                padding: 16,
                                color: '#fff'
                            }}>
                                <div style={{ fontWeight: 700, marginBottom: 8 }}>{cur}</div>
                                <div>Количество депозитов: <b>{data.count}</b></div>
                                <div>Общая сумма: <b>{data.total.toFixed(2)} {cur}</b></div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Таблица пользователей */}
            <div className="referral-table-block" style={{ marginTop: 40 }}>
                <div className="referral-table-title">Привлечённые пользователи</div>
                
                {users.length > 0 ? (
                    <>
                        <div className="referral-table-wrapper">
                            <table className="referral-table">
                                <thead>
                                    <tr>
                                        <th>Email</th>
                                        <th>Дата регистрации</th>
                                        <th>Депозитов</th>
                                        <th>Сумма депозитов</th>
                                        <th>Выплата получена</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedUsers.map((user) => (
                                        <tr key={user.id}>
                                            <td>{user.email}</td>
                                            <td>{new Date(user.created_at).toLocaleDateString()}</td>
                                            <td>{user.depositCount}</td>
                                            <td>
                                                {Object.entries(user.depositsByCurrency || {}).map(([cur, data]) => (
                                                    <div key={cur}>{data.total.toFixed(2)} {cur}</div>
                                                ))}
                                                {user.depositCount === 0 && <span style={{ color: '#999' }}>Нет депозитов</span>}
                                            </td>
                                            <td>
                                                {user.hasPayout ? (
                                                    <span style={{ color: '#28bc10' }}>✓ Да</span>
                                                ) : (
                                                    <span style={{ color: user.hasDeposit ? '#ffb300' : '#999' }}>
                                                        {user.hasDeposit ? 'Нет' : 'Нет депозита'}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Пагинация */}
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 20 }}>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: 8,
                                        border: 'none',
                                        background: currentPage === 1 ? '#eaeaea' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: currentPage === 1 ? '#999' : '#fff',
                                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                        fontWeight: 600
                                    }}
                                >
                                    ← Предыдущая
                                </button>
                                <span style={{ color: '#fff', fontWeight: 600 }}>
                                    Страница {currentPage} из {totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: 8,
                                        border: 'none',
                                        background: currentPage === totalPages ? '#eaeaea' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: currentPage === totalPages ? '#999' : '#fff',
                                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                        fontWeight: 600
                                    }}
                                >
                                    Следующая →
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="referral-no-data-block">
                        <div className="referral-no-data-icon">👥</div>
                        <div className="referral-no-data-text">Пока нет привлечённых пользователей</div>
                    </div>
                )}
            </div>

            {/* История выплат */}
            {withdrawals && withdrawals.length > 0 && (
                <div style={{ marginTop: 40 }}>
                    <h3 style={{ color: '#fff', marginBottom: 16 }}>История выплат</h3>
                    <div className="referral-table-wrapper">
                        <table className="referral-table">
                            <thead>
                                <tr>
                                    <th>ID заявки</th>
                                    <th>Сумма</th>
                                    <th>Валюта</th>
                                    <th>Пользователей</th>
                                    <th>Статус</th>
                                    <th>Дата создания</th>
                                </tr>
                            </thead>
                            <tbody>
                                {withdrawals.map((w) => (
                                    <tr key={w.id}>
                                        <td>{w.ext_id}</td>
                                        <td>{w.amount}</td>
                                        <td>{w.currency}</td>
                                        <td>{w.users_count}</td>
                                        <td>
                                            <span style={{
                                                color: w.status === 'SUCCESSFUL' ? '#28bc10' : 
                                                       w.status === 'DENIED' ? '#ff4b4b' : '#ffb300'
                                            }}>
                                                {w.status === 'SUCCESSFUL' ? 'Выплачено' : 
                                                 w.status === 'DENIED' ? 'Отклонено' : 'Ожидание'}
                                            </span>
                                        </td>
                                        <td>{new Date(w.created_at).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {error && <p style={{ color: "#ff4b4b", fontWeight: 600, marginTop: 20 }}>{error}</p>}
        </div>
    );
};

export default CPAPartner;

